import { AppId } from '@rockwell-automation-inc/common-utils';
import {
  ColDef,
  IDateFilterParams,
  IRowNode,
  ISetFilterParams,
  ProcessCellForExportParams,
  ValueFormatterParams,
} from 'ag-grid-community';
import { CustomDatePipe } from '../pipes/date/custom-date.pipe';

interface EntitlementHeaders {
  header: unknown;
  subheader: unknown;
}

type ClipboardRowData = Record<string, unknown>;
const customDatePipe = new CustomDatePipe();

export function processCellForClipboard(params: ProcessCellForExportParams): unknown {
  const node = params.node;
  const rowData = node?.data as ClipboardRowData | undefined;
  if (!node || !rowData) {
    return params.value;
  }

  const columnName = params.column.getColDef().field;
  switch (columnName) {
    case 'serviceKind': {
      const entitlementHeaders = rowData['entitlementHeaders'] as EntitlementHeaders;
      return `${entitlementHeaders.header} ${entitlementHeaders.subheader}`;
    }
    case 'effectiveDate':
    case 'expiryDate':
    case 'reservedOn':
    case 'redeemBefore':
    case 'redeemedOn':
    case 'expiresOn':
      return customDatePipe.transform(rowData[columnName], 'mediumDate', 'en-US');
    case 'creditsActivity':
      return (rowData['tokenConsumptionMapping'] as (node: IRowNode) => unknown)(node);
    default:
      return params.value;
  }
}

export interface GridColumnOptions {
  filterable?: boolean;
  sortable?: boolean;
}

export interface DateTimeColumnOptions extends GridColumnOptions {
  filterParams?: Partial<IDateFilterParams>;
}

export interface CellRendererColumnOptions extends GridColumnOptions {
  cellRendererParams?: ColDef['cellRendererParams'];
  filterParams?: ColDef['filterParams'];
}

export function agDateComparator(filterDate: Date, cellValue: string | null): number {
  if (cellValue === null) {
    return -1;
  }
  const cellDate = new Date(cellValue);
  cellDate.setHours(0, 0, 0, 0);
  if (filterDate.getTime() === cellDate.getTime()) {
    return 0;
  }
  if (cellDate.getTime() <= filterDate.getTime()) {
    return -1;
  }
  if (cellDate.getTime() >= filterDate.getTime()) {
    return 1;
  }
  return 0;
}

export class SharedGridColumnDefs {
  private static withTextFilter(columnDef: ColDef): ColDef {
    return {
      ...columnDef,
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['contains', 'notContains', 'equals', 'notEqual', 'startsWith', 'endsWith'],
        maxNumConditions: 1,
      },
    };
  }

  static textCol(field: string, headerName: string, options: GridColumnOptions = {}): ColDef {
    const { filterable = true, sortable = true } = options;
    const columnDef: ColDef = {
      field,
      headerName,
      sortable,
      minWidth: 200,
    };
    return filterable ? this.withTextFilter(columnDef) : columnDef;
  }

  static cellRendererCol(
    field = '',
    headerName = '',
    cellRenderer: ColDef['cellRenderer'],
    options: CellRendererColumnOptions = {},
  ): ColDef {
    const { cellRendererParams = {}, filterable = false, filterParams = {}, sortable = false } = options;
    return {
      field,
      headerName,
      cellRenderer,
      cellRendererParams: { ...cellRendererParams },
      sortable,
      filter: filterable ? 'agSetColumnFilter' : undefined,
      filterParams: { ...filterParams },
      minWidth: 150,
    };
  }

  static numberCol(field: string, headerName: string, options: GridColumnOptions = {}): ColDef {
    const { filterable = true, sortable = true } = options;
    return {
      field,
      headerName,
      sortable,
      filter: filterable ? 'agNumberColumnFilter' : undefined,
      filterParams: {
        filterOptions: [
          'equals',
          'notEqual',
          'lessThan',
          'lessThanOrEqual',
          'greaterThan',
          'greaterThanOrEqual',
          'inRange',
        ],
        maxNumConditions: 1,
        inRangeInclusive: true,
      },
    };
  }

  static checkboxCol(field: string, headerName: string): ColDef {
    return {
      field,
      headerName,
      minWidth: 150,
      suppressHeaderMenuButton: true,
      filter: false,
      sortable: false,
    };
  }
  static dateTimeCol(field: string, headerName: string, options: DateTimeColumnOptions = {}): ColDef {
    const { filterable = true, sortable = true, filterParams = {} } = options;
    return {
      field,
      headerName,
      sortable,
      filter: filterable ? 'agDateColumnFilter' : undefined,
      filterParams: {
        filterOptions: ['lessThanOrEqual', 'greaterThanOrEqual', 'inRange'],
        maxNumConditions: 1,
        inRangeInclusive: true,
        // AG Grid infers ISO timestamps as 'dateTimeString' and would otherwise render a date-time picker.
        includeTime: false,
        ...filterParams,
      },
    };
  }
  static withSetFilter(columnDef: ColDef, filterParams: ISetFilterParams): ColDef {
    return {
      ...columnDef,
      filter: 'agSetColumnFilter',
      filterParams,
    };
  }

  static extend(...columnDefs: ColDef[]): ColDef {
    return Object.assign({}, ...columnDefs);
  }
}

interface SharedApplicationCard {
  appName: string;
  appId: AppId;
  isDefaultTrialEnabled?: boolean;
}

const previewAppIds = new Set(['Fiix', 'EPlan', 'Plex', 'CDS', 'FactoryTalkMotionAnalyzer', 'Advisor']);

export const applications: SharedApplicationCard[] = [
  { appName: 'FactoryTalk Vault', appId: AppId.Vault },
  { appName: 'FactoryTalk Remote Access', appId: AppId.SecureRemoteAccess },
  { appName: 'FactoryTalk Design Studio', appId: AppId.IDE, isDefaultTrialEnabled: true },
  { appName: 'Foo (Integration Sample)', appId: AppId.FooService },
  { appName: 'FactoryTalk Edge Manager', appId: AppId.EaaS },
  { appName: 'FT Twin Studio', appId: AppId.TwinStudio },
  { appName: 'FactoryTalk Optix', appId: AppId.FTOptix },
  { appName: 'Asset Intelligence', appId: AppId.AssetPerformanceMonitor },
  { appName: 'FactoryTalk DataMosaix', appId: AppId.DataMosaix },
  { appName: 'FactoryTalk Energy Manager', appId: AppId.EnergyManager },
  { appName: 'Batch Performance Analytics', appId: AppId.BatchPerformanceAnalytics },
  { appName: 'Enterprise FTAC', appId: AppId.AssetCenter },
  { appName: 'PlantPAx Analytics', appId: AppId.PPaxAnalytics },
  { appName: 'Converged Data Services', appId: AppId.CDS },
  { appName: 'FactoryTalk Analytics VisionAI', appId: AppId.Elementary },
];

export const allApps = new Map<AppId | 'UniversalCredits', SharedApplicationCard>(
  applications.map((application) => [application.appId, application]),
);

export function getProductFamily(serviceKind: AppId | 'UniversalCredits'): string {
  if (serviceKind === 'UniversalCredits') {
    return 'Universal Credits';
  }
  return allApps.get(serviceKind)?.appName ?? serviceKind;
}

export function getAppIds(): string[] {
  const appIds = Array.from(allApps.values())
    .filter((application) => !previewAppIds.has(application.appId))
    .map((application) => application.appId);
  return [...appIds, 'UniversalCredits'];
}

export function getTrialsAppIds(): string[] {
  const appIds = Array.from(allApps.values())
    .filter((application) => !previewAppIds.has(application.appId) && application.isDefaultTrialEnabled)
    .map((application) => application.appId);
  return [...appIds, 'UniversalCredits'];
}

export function getEntitlementStatus(status: string): string {
  return status === 'Completed' ? 'Expired' : status;
}

export function formatCombineType(params: ValueFormatterParams): string {
  const rawValue = params.value;
  if (rawValue === null || rawValue === undefined) {
    return '';
  }
  const value = String(rawValue);
  if (value === 'credit') {
    return 'Universal Credits';
  }
  if (value === 'addon') {
    return 'Add-On';
  }
  return value.charAt(0).toUpperCase() + value.slice(1);
}

export function formatDuration(params: ValueFormatterParams): string {
  const rawValue = params.value;
  if (rawValue === null || rawValue === undefined) {
    return '';
  }
  const value = String(rawValue);
  return value.charAt(0).toUpperCase() + value.slice(1);
}

export function greyOutCellStyle(params: { data?: { status?: string } }): { color: string } | null {
  const status = params.data?.status?.toLowerCase();
  return ['cancelled', 'completed', 'ended', 'expired'].includes(status ?? '') ? { color: '#5D6063' } : null;
}
